// NOMBASE — Client-side perf reporter.
//
// Captures user-PERCEIVED navigation timings and posts them, fire-and-
// forget, to `POST /api/perf/client-timing`. The Admin Portal reads
// the aggregated buffer via `GET /api/admin/_diag/client-timings`
// (admin route removed Feb 2026; the perf endpoint itself still
// accepts POSTs from the browser).
//
// What we measure:
//   • nav_ms  — wall-clock time from a route change to its first useful
//               paint. For the very first load we use
//               `PerformanceNavigationTiming.domContentLoadedEventEnd`,
//               for SPA navs we use `performance.now()` deltas.
//   • ttfb_ms — `responseStart - requestStart` from the navigation
//               entry (only present on the very first load).
//   • fcp_ms  — First Contentful Paint (only on initial load).
//   • lcp_ms  — Largest Contentful Paint (initial load; observer is
//               disconnected after the first LCP entry).
//
// Why not Web Vitals lib?
//   `web-vitals` is a great package but adds ~3 KB gzip + an import
//   surface. For our needs the native Performance API is enough and
//   the bundle stays lean.
//
// Privacy: no PII in the payload. The backend attaches the
// Clerk-derived `tenant_id` / `user_id` from `get_current_user`, so
// the browser never even sees those values.
import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';

const API = process.env.REACT_APP_BACKEND_URL;
const ENDPOINT = `${API}/api/perf/client-timing`;
const REPORT_THRESHOLD_MS = 50;   // skip near-instant navs (cache hits)

const hdrs = () => ({ 'Content-Type': 'application/json' });

// Fire-and-forget POST. We deliberately do NOT use `navigator.sendBeacon`
// because beacons cannot send cookies cross-origin reliably, and the
// `/api/perf/client-timing` endpoint requires an authenticated session.
// `fetch` with `credentials: 'include'` + `keepalive: true` survives
// page-hide / route change and lets the browser attach the
// `nombase_session` cookie automatically.
//
// Back-off: once a report 502/503/504s, pause future reports for the
// rest of the session. The dashboard cold-load already has tighter,
// user-facing GETs competing for the worker pool; piling perf POSTs
// on top of that is exactly what we don't want during a cold-start
// window. The first 5xx is enough signal that the backend is busy.
let _reportingDisabled = false;
function report(payload) {
  if (_reportingDisabled) return;
  try {
    const body = JSON.stringify(payload);
    const headers = hdrs();
    fetch(ENDPOINT, { credentials: 'include', method: 'POST', headers, body, keepalive: true })
      .then((res) => {
        // 401 → unauthenticated session (cookie not yet set or expired);
        // disable for the rest of the session so we don't spam the
        // console. 5xx → back-pressure; same treatment.
        if (res && (res.status === 401 || res.status >= 500)) {
          _reportingDisabled = true;
        }
      })
      .catch(() => { _reportingDisabled = true; });
  } catch { /* never break the app for telemetry */ }
}

// ─── Initial-load capture ──────────────────────────────────────────────
// Reads the Navigation Timing entry + Paint Timing entries + observes
// the first LCP. Runs ONCE, after `load`.
function captureInitialLoad(route) {
  try {
    if (typeof performance === 'undefined') return;

    const navEntry = performance.getEntriesByType('navigation')[0];
    let ttfb_ms = null;
    let nav_ms  = null;
    if (navEntry) {
      ttfb_ms = navEntry.responseStart - navEntry.requestStart;
      nav_ms  = navEntry.domContentLoadedEventEnd - navEntry.startTime;
    }

    const fcpEntry = performance.getEntriesByType('paint')
      .find((p) => p.name === 'first-contentful-paint');
    const fcp_ms = fcpEntry ? fcpEntry.startTime : null;

    // LCP — wait for the LARGEST contentful paint via PerformanceObserver,
    // then post + disconnect. Falls back gracefully if unsupported.
    let lcp_ms = null;
    let posted = false;
    const post = () => {
      if (posted) return;
      posted = true;
      if ((nav_ms || 0) >= REPORT_THRESHOLD_MS || (lcp_ms || 0) >= REPORT_THRESHOLD_MS) {
        report({
          route, nav_ms, ttfb_ms, fcp_ms, lcp_ms,
          url: window.location?.href,
        });
      }
    };

    if (typeof PerformanceObserver !== 'undefined') {
      try {
        const po = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          const last = entries[entries.length - 1];
          if (last) lcp_ms = last.startTime;
        });
        po.observe({ type: 'largest-contentful-paint', buffered: true });
        // Finalize after either page hide or 4s — whichever comes first.
        const finalize = () => { po.disconnect(); post(); };
        window.addEventListener('visibilitychange', () => {
          if (document.visibilityState === 'hidden') finalize();
        }, { once: true });
        setTimeout(finalize, 4000);
        return;
      } catch { /* PO unsupported on older browsers — fall through */ }
    }
    post();
  } catch { /* swallow */ }
}

// ─── SPA navigation capture ────────────────────────────────────────────
// Pure `performance.now()` delta between `useLocation` route change and
// the very next animation frame after mount (proxy for "first paint of
// the new route's content").
export default function ClientPerfReporter() {
  const location = useLocation();
  const initialDone = useRef(false);
  const lastNavStart = useRef(performance.now());
  const lastRoute = useRef(location.pathname);

  useEffect(() => {
    // First effect ever: harvest the page-load metrics.
    if (!initialDone.current) {
      initialDone.current = true;
      // Defer the initial-load capture by 3s so we don't pile a perf
      // POST onto the dashboard's cold-load fan-out (auth + dashboard
      // + procurement + plan + activity + messages all firing in
      // parallel against a single-worker uvicorn). 3s is well past
      // typical first-render and well within the LCP-observer 4s
      // finalisation window. Worst case the initial report drops —
      // not a problem for telemetry that's rolling-buffered anyway.
      const t = setTimeout(() => captureInitialLoad(location.pathname), 3000);
      lastRoute.current = location.pathname;
      lastNavStart.current = performance.now();
      return () => clearTimeout(t);
    }

    // Subsequent route changes: measure mount-paint latency.
    if (location.pathname === lastRoute.current) return;
    const route = location.pathname;
    const navStart = lastNavStart.current;
    // Wait two frames so React has committed + the browser has painted.
    let raf1 = 0, raf2 = 0;
    raf1 = window.requestAnimationFrame(() => {
      raf2 = window.requestAnimationFrame(() => {
        const nav_ms = performance.now() - navStart;
        if (nav_ms >= REPORT_THRESHOLD_MS) {
          report({ route, nav_ms, url: window.location?.href });
        }
      });
    });

    lastRoute.current = route;
    lastNavStart.current = performance.now();

    return () => {
      window.cancelAnimationFrame(raf1);
      window.cancelAnimationFrame(raf2);
    };
  }, [location.pathname]);

  return null;
}
